import { GroupBlockData } from "../interfaces/groupblock";
import { useCallback, useMemo } from "react";
import { Document } from "../../document";
import { useMultipleGroupBlocks } from "../index";
import useAnnotations from "../../annotation";
import useCategorizationAnnotations from "../../metadata/hooks/useCategorizationAnnotations";
import { useGroupBlockCategorizations } from "./useGroupBlockCategorizations";

const useGroupBlock = (
  multipleGroupBlocks: boolean,
  groupBlock: GroupBlockData,
  activeDocument?: Document,
) => {
  const { groupBlockEntityTypes } = groupBlock;

  const { removeGroupBlock, resetMultipleGroupBlock } =
    useMultipleGroupBlocks();

  const { clearAnnotationsForEntities, clearAnnotationsForEntity } =
    useAnnotations({
      documentId: activeDocument?.id,
    });

  const {
    modifyCategorizationAnnotations,
    removeCategorizationAnnotationsByCategorizations,
  } = useCategorizationAnnotations();

  const entityTypesSorted = useMemo(
    () =>
      groupBlockEntityTypes
        ? [...groupBlockEntityTypes].sort((a, b) => a.index - b.index)
        : [],
    [groupBlockEntityTypes],
  );

  const entityIds = useMemo(
    () => entityTypesSorted.map((entityType) => entityType.entityType.id),
    [entityTypesSorted],
  );

  const { categorizations } = useGroupBlockCategorizations(groupBlock.id);

  const removeGroupBlockAndAnnotations = useCallback(
    (index: number) => {
      removeGroupBlock(groupBlock);
      clearAnnotationsForEntities(entityIds, index, true, multipleGroupBlocks);
      modifyCategorizationAnnotations(
        categorizations,
        index,
        true,
        activeDocument?.id,
      );
    },
    [
      removeGroupBlock,
      groupBlock,
      entityIds,
      clearAnnotationsForEntities,
      activeDocument?.id,
      modifyCategorizationAnnotations,
      categorizations,
      multipleGroupBlocks,
    ],
  );

  const removeAnnotations = useCallback(
    (index: number) => {
      clearAnnotationsForEntities(entityIds, index, false, multipleGroupBlocks);
      modifyCategorizationAnnotations(
        categorizations,
        index,
        false,
        activeDocument?.id,
      );
    },
    [
      entityIds,
      clearAnnotationsForEntities,
      modifyCategorizationAnnotations,
      activeDocument?.id,
      categorizations,
      multipleGroupBlocks,
    ],
  );

  const removeGroupBlocksForEntityAndAnnotations = useCallback(
    (index: number) => {
      resetMultipleGroupBlock(groupBlock);
      clearAnnotationsForEntity(entityIds, index, multipleGroupBlocks);
      removeCategorizationAnnotationsByCategorizations(
        categorizations,
        activeDocument?.id,
      );
    },
    [
      groupBlock,
      entityIds,
      resetMultipleGroupBlock,
      clearAnnotationsForEntity,
      removeCategorizationAnnotationsByCategorizations,
      activeDocument?.id,
      categorizations,
      multipleGroupBlocks,
    ],
  );

  return {
    entityIds,
    entityTypesSorted,
    removeGroupBlockAndAnnotations,
    removeAnnotations,
    removeGroupBlocksForEntityAndAnnotations,
  };
};

export default useGroupBlock;
